// apps/office/src/pages/office/InvoiceForm.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Textarea } from '../../components/ui/textarea';
import {
  FileText, Save, Send, CheckCircle, Loader2, AlertCircle,
  Receipt, Trash2, Plus, Percent, DollarSign, Lock, ArrowLeft,
} from 'lucide-react';
import pb from '../../lib/pocketbaseClient';
import { generateInvoiceNumber as genInvNum } from '../../lib/idGenerator';
import { useAuth } from '../../contexts/AuthContext';

const InvoiceForm = () => {
  const { invoiceId, quotationId: urlQuotationId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const isEditing = !!invoiceId;

  const isAdmin = ['admin', 'super_admin'].includes(currentUser?.role);
  const canEditItems = isAdmin;

  // Form state
  const [selectedQuotationId, setSelectedQuotationId] = useState(urlQuotationId || '');
  const [items, setItems] = useState([]);
  const [discountType, setDiscountType] = useState('amount');
  const [discountValue, setDiscountValue] = useState(0);
  const [taxRate, setTaxRate] = useState(18);
  const [notes, setNotes] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
  const [dueDate, setDueDate] = useState(
    new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  );
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Package selector
  const [selectedBuilding, setSelectedBuilding] = useState('');
  const [selectedPackage, setSelectedPackage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showPackageSelector, setShowPackageSelector] = useState(false);

  /* --- Load existing invoice (edit mode) --- */
  const { data: existingInvoice, isLoading: existingLoading } = useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: () => pb.collection('invoices').getOne(invoiceId, { expand: 'leadId,quotationId' }),
    enabled: isEditing,
  });

  /* --- Fetch Quotations (Architecture) --- */
  const { data: allQuotations = [], isLoading: quotationsLoading } = useQuery({
    queryKey: ['quotations-invoice-form'],
    queryFn: () => pb.collection('quotations').getFullList({
      sort: '-created',
      filter: 'status = "Approved" && isLatest = true',
      expand: 'clientJobId',
    }),
    enabled: !isEditing,
  });

  /* --- Fetch Payments --- */
  const { data: payments = [], isLoading: paymentsLoading } = useQuery({
    queryKey: ['payments-invoice-form'],
    queryFn: () => pb.collection('payments').getFullList({ filter: 'status = "Completed"' }),
    enabled: !isEditing,
  });

  /* --- Fetch Existing Invoices --- */
  const { data: existingInvoices = [], isLoading: invoicesLoading } = useQuery({
    queryKey: ['invoices-list-check'],
    queryFn: () => pb.collection('invoices').getFullList({ fields: 'quotationId' }),
    enabled: !isEditing,
  });

  /* --- Filter Approved Quotations: Fully Paid & No Invoice --- */
  const approvedQuotations = useMemo(() => {
    if (quotationsLoading || paymentsLoading || invoicesLoading) return [];

    const invoicedQuoteIds = new Set(existingInvoices.map(inv => inv.quotationId).filter(Boolean));

    return allQuotations.filter(q => {
      // 1. Skip if invoice already created
      if (invoicedQuoteIds.has(q.id)) return false;

      // 2. Calculate total paid
      const paidAmount = payments
        .filter(p => p.quotationId === q.id)
        .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

      // 3. Check if 100% paid
      const totalAmount = Number(q.total) || 0;
      return totalAmount > 0 && paidAmount >= (totalAmount - 0.1);
    });
  }, [allQuotations, payments, existingInvoices, quotationsLoading, paymentsLoading, invoicesLoading]);

  /* --- Selected quotation for create mode --- */
  const { data: selectedQuotation } = useQuery({
    queryKey: ['quotation', selectedQuotationId],
    queryFn: () => pb.collection('quotations').getOne(selectedQuotationId, { expand: 'clientJobId' }),
    enabled: !!selectedQuotationId && !isEditing,
  });

  /* --- Price list for item editor --- */
  const { data: priceItems = [], isLoading: priceLoading } = useQuery({
    queryKey: ['priceList'],
    queryFn: () => pb.collection('price_list_items').getFullList({ sort: 'buildingType,name' }),
    enabled: canEditItems,
  });

  /* --- Load data into form � edit mode --- */
  useEffect(() => {
    if (!isEditing || !existingInvoice || loaded) return;
    const q = existingInvoice.expand?.quotationId || {};

    setInvoiceNumber(existingInvoice.invoiceNumber || '');
    setSelectedQuotationId(existingInvoice.quotationId || '');
    setNotes(existingInvoice.notes || '');
    setInvoiceDate(existingInvoice.invoiceDate?.split('T')[0] || new Date().toISOString().split('T')[0]);
    setDueDate(existingInvoice.dueDate?.split('T')[0] || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
    setDiscountValue(existingInvoice.discountAmount || 0);
    setTaxRate(existingInvoice.taxRate || q.taxRate || 18);
    setDiscountType('amount');

    setItems(q.items || []);
    setLoaded(true);
  }, [existingInvoice, isEditing, loaded]);

  /* --- Load data � create mode --- */
  useEffect(() => {
    if (isEditing || !selectedQuotation || loaded) return;
    setItems(selectedQuotation.items || []);
    setNotes(selectedQuotation.notes || '');
    setDiscountValue(selectedQuotation.discountAmount || 0);
    setTaxRate(selectedQuotation.taxRate || 18);
    setDiscountType(selectedQuotation.discountType || 'amount');
    setLoaded(true);
  }, [selectedQuotation, isEditing, loaded]);

  /* --- Client info --- */
  const quotation = isEditing ? existingInvoice?.expand?.quotationId : selectedQuotation;
  const lead = isEditing ? existingInvoice?.expand?.leadId : selectedQuotation?.expand?.clientJobId;

  const clientName = lead?.name || quotation?.clientName || '-';
  const clientPhone = lead?.phone || quotation?.clientPhone || '-';
  const clientEmail = lead?.email || quotation?.clientEmail || '-';
  const clientAddress = lead?.address || quotation?.clientAddress || '-';
  const clientJobId = lead?.jobId || '';
  const quotationNumber = quotation?.quoteNumber || '-';
  const invoiceLeadId = lead?.id || existingInvoice?.leadId || null;
  const invoiceQuotationId = quotation?.id || selectedQuotationId || existingInvoice?.quotationId || null;

  /* --- Package filters --- */
  const filteredPackages = priceItems.filter((pkg) => {
    const matchesBuilding = !selectedBuilding || selectedBuilding === 'All' || pkg.buildingType === selectedBuilding;
    const matchesPackage = !selectedPackage || selectedPackage === 'All' || pkg.packageType === selectedPackage;
    const matchesSearch = !searchTerm || pkg.name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesBuilding && matchesPackage && matchesSearch;
  });

  const buildingOptions = useMemo(
    () => ['All', ...new Set(priceItems.map((p) => p.buildingType).filter(Boolean))],
    [priceItems]
  );
  const packageOptions = useMemo(
    () => ['All', ...new Set(priceItems.map((p) => p.packageType).filter(Boolean))],
    [priceItems]
  );

  /* --- Item helpers --- */
  const addItemFromPackage = (pkg) => {
    setItems([
      ...items,
      {
        id: Date.now(),
        priceItemId: pkg.id,
        name: pkg.name,
        description: pkg.details || '',
        unit: pkg.unit || 'Package',
        quantity: 1,
        rate: pkg.sellingPrice,
        amount: pkg.sellingPrice,
        mrp: pkg.mrp,
      },
    ]);
    setShowPackageSelector(false);
    toast.success(`Added: ${pkg.name}`);
  };

  const updateItem = (index, fieldName, value) => {
    const newItems = [...items];
    if (fieldName === 'quantity' || fieldName === 'rate') {
      const num = parseFloat(value) || 0;
      newItems[index][fieldName] = num;
      newItems[index].amount = newItems[index].rate * newItems[index].quantity;
    } else {
      newItems[index][fieldName] = value;
    }
    setItems(newItems);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  /* --- Totals --- */
  const subtotal = items.reduce((sum, i) => sum + (i.amount || 0), 0);
  const discountAmount =
    discountType === 'percentage' ? (subtotal * discountValue) / 100 : discountValue;
  const taxAmount = ((subtotal - discountAmount) * taxRate) / 100;
  const totalAmount = subtotal - discountAmount + taxAmount;

  /* --- Generate invoice number --- */
  const genInvoiceNumber = async () => {
    return await genInvNum();
  };

  /* --- Save --- */
  const handleSave = async (statusValue) => {
    if (!isEditing && !selectedQuotationId) {
      toast.error('Select an approved quotation first');
      return;
    }
    if (items.length === 0) {
      toast.error('At least one item is required');
      return;
    }

    setIsSaving(true);
    try {
      let invNumber;
      if (isEditing) {
        invNumber = invoiceNumber;
      } else {
        let attempts = 0;
        invNumber = null;
        while (attempts < 10 && !invNumber) {
          const candidate = await genInvoiceNumber();
          try {
            const dup = await pb.collection('invoices').getList(1, 1, {
              filter: `invoiceNumber = "${candidate}"`,
            });
            if (dup.items.length === 0) invNumber = candidate;
          } catch (e) {
            invNumber = candidate;
          }
          attempts++;
        }
        if (!invNumber) throw new Error('Could not generate unique invoice number after 10 attempts');
      }

      const payload = {
        invoiceNumber: invNumber,
        quotationId: invoiceQuotationId,
        leadId: invoiceLeadId,
        invoiceDate,
        dueDate,
        discountAmount: discountAmount || 0,
        taxAmount: taxAmount || 0,
        totalAmount: totalAmount || 0,
        notes: notes || '',
        field: statusValue,
      };

      let record;
      if (isEditing) {
        record = await pb.collection('invoices').update(invoiceId, payload);
        toast.success(`Invoice ${invNumber} updated`);
      } else {
        record = await pb.collection('invoices').create(payload);
        toast.success(`Invoice ${invNumber} created`);

        try {
          await pb.collection('quotations').update(selectedQuotationId, { status: 'Invoiced' });
        } catch {}
      }

      queryClient.invalidateQueries(['invoices']);
      queryClient.invalidateQueries(['quotations']);
      navigate('/office/invoices');
    } catch (err) {
      console.error('Save error:', err);
      let msg = err.message || 'Failed to save';
      if (err.originalError?.data?.message) msg = err.originalError.data.message;
      if (err.originalError?.data?.data) {
        const fields = Object.entries(err.originalError.data.data)
          .map(([f, e]) => `${f}: ${e.message}`)
          .join('; ');
        if (fields) msg += ` -> ${fields}`;
      }
      toast.error(msg);
    } finally {
      setIsSaving(false);
    }
  };

  const formatPrice = (p) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency', currency: 'INR', maximumFractionDigits: 2,
    }).format(p || 0);

  /* --- Loading --- */
  if (isEditing && existingLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
        <p className="text-slate-500 mt-3">Loading invoice...</p>
      </div>
    );
  }

  if (!isEditing && (quotationsLoading || paymentsLoading || invoicesLoading) && !urlQuotationId) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
        <p className="text-slate-500 mt-3">Checking approved quotations & payments...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-green-600 via-emerald-600 to-teal-500 p-8 shadow-xl">
        <div className="absolute top-0 right-0 -mt-16 -mr-16 w-64 h-64 bg-white/10 rounded-full blur-2xl" />
        <div className="relative flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/office/invoices')}
              className="text-white/90 hover:text-white hover:bg-white/20"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center gap-2">
                <Receipt className="w-8 h-8" /> {isEditing ? `Edit ${invoiceNumber || 'Invoice'}` : 'New Invoice'}
              </h1>
              <p className="text-white/80">
                {isEditing ? 'Update items, discount, tax and details' : 'Create invoice from fully paid quotation'}
              </p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={() => handleSave(isEditing ? (existingInvoice?.field || 'Draft') : 'Draft')}
              disabled={isSaving}
              variant="outline"
              className="bg-white/20 text-white border-white/30 hover:bg-white/30 hover:text-white gap-2"
            >
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Changes
            </Button>
            {!isEditing && (
              <Button
                onClick={() => handleSave('Pending Approval')}
                disabled={isSaving}
                className="bg-white text-green-600 hover:bg-green-50 shadow-lg gap-2"
              >
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Save &amp; Send for Approval
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Quotation picker (create mode only) */}
      {!isEditing && (
        <Card className="border-0 shadow-xl">
          <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-indigo-100">
            <CardTitle className="flex items-center gap-2 text-indigo-800">
              <CheckCircle className="w-5 h-5" /> Select Fully Paid Quotation
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {approvedQuotations.length === 0 ? (
              <div className="text-center py-8">
                <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
                <p className="text-slate-700 font-medium">No fully paid quotations available for invoicing</p>
                <p className="text-sm text-slate-500 mt-1">Quotations must be 100% paid and must not have an invoice generated yet.</p>
                <Button variant="outline" onClick={() => navigate('/office/payments')} className="mt-4">
                  Go to Payments
                </Button>
              </div>
            ) : (
              <div>
                <Label className="text-indigo-700 font-medium">Quotation *</Label>
                <Select value={selectedQuotationId} onValueChange={setSelectedQuotationId}>
                  <SelectTrigger className="border-indigo-200 mt-1">
                    <SelectValue placeholder="Select a fully paid quotation..." />
                  </SelectTrigger>
                  <SelectContent>
                    {approvedQuotations.map((q) => (
                      <SelectItem key={q.id} value={q.id}>
                        <span className="font-mono text-indigo-600 font-semibold">{q.quoteNumber}</span>
                        <span className="text-slate-300 mx-2">|</span>
                        <span className="font-medium text-slate-700">{q.clientName || 'Unknown'}</span>
                        <span className="text-slate-300 mx-2">|</span>
                        <span className="text-green-600 font-bold">{formatPrice(q.total)}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Show form when quotation loaded */}
      {(quotation || isEditing) && (
        <>
          {/* Client Info */}
          <Card className="border-0 shadow-xl">
            <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-100">
              <CardTitle className="text-green-800">Client Information</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-green-700">Client</Label>
                  <Input value={clientName} disabled className="bg-slate-50" />
                </div>
                <div>
                  <Label className="text-green-700">Job ID</Label>
                  <Input value={clientJobId || '-'} disabled className="bg-slate-50 font-mono" />
                </div>
                <div>
                  <Label className="text-green-700">Mobile</Label>
                  <Input value={clientPhone} disabled className="bg-slate-50" />
                </div>
                <div>
                  <Label className="text-green-700">Email</Label>
                  <Input value={clientEmail} disabled className="bg-slate-50" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-green-700">Address</Label>
                  <Input value={clientAddress} disabled className="bg-slate-50" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-indigo-700">Source Quotation</Label>
                  <Input value={quotationNumber} disabled className="bg-indigo-50 border-indigo-200 font-mono text-indigo-700" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Items */}
          <Card className="border-0 shadow-xl">
            <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-100 flex flex-row justify-between items-center">
              <CardTitle className="text-green-800 flex items-center gap-2">
                Items {!canEditItems && <Lock className="w-4 h-4 text-slate-400" />}
              </CardTitle>
              {canEditItems ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPackageSelector(true)}
                  className="border-green-200 text-green-600 hover:bg-green-50"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Item
                </Button>
              ) : (
                <span className="text-xs text-slate-400">Only admin can edit items</span>
              )}
            </CardHeader>
            <CardContent className="p-6">
              <Table>
                <TableHeader className="bg-slate-50">
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead>Unit</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Rate</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    {canEditItems && <TableHead className="text-right"></TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item, idx) => (
                    <TableRow key={item.id || idx}>
                      <TableCell className="font-medium">{item.name}</TableCell>
                      <TableCell>{item.unit}</TableCell>
                      <TableCell className="text-right">
                        {canEditItems ? (
                          <Input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                            className="w-20 text-right"
                            min="1"
                          />
                        ) : (
                          item.quantity
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {canEditItems ? (
                          <Input
                            type="number"
                            value={item.rate}
                            onChange={(e) => updateItem(idx, 'rate', e.target.value)}
                            className="w-28 text-right"
                          />
                        ) : (
                          formatPrice(item.rate)
                        )}
                      </TableCell>
                      <TableCell className="text-right font-semibold">{formatPrice(item.amount)}</TableCell>
                      {canEditItems && (
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => removeItem(idx)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {items.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={canEditItems ? 6 : 5} className="text-center py-8 text-slate-500">
                        No items
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Discount & Tax */}
          {items.length > 0 && (
            <Card className="border-0 shadow-xl">
              <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-200">
                <CardTitle className="text-amber-800 flex items-center gap-2">
                  Discount &amp; Tax {!canEditItems && <Lock className="w-4 h-4 text-slate-400" />}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-3">
                    <Label className="text-amber-700">Discount</Label>
                    {canEditItems ? (
                      <>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant={discountType === 'percentage' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setDiscountType('percentage')}
                            className="flex-1"
                          >
                            <Percent className="w-3 h-3 mr-1" /> %
                          </Button>
                          <Button
                            type="button"
                            variant={discountType === 'amount' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setDiscountType('amount')}
                            className="flex-1"
                          >
                            <DollarSign className="w-3 h-3 mr-1" /> ?
                          </Button>
                        </div>
                        <Input
                          type="number"
                          value={discountValue}
                          onChange={(e) => setDiscountValue(parseFloat(e.target.value) || 0)}
                          className="border-amber-200"
                        />
                      </>
                    ) : (
                      <p className="text-slate-700 font-medium">
                        {discountAmount > 0 ? formatPrice(discountAmount) : '-'}
                      </p>
                    )}
                  </div>
                  <div className="space-y-3">
                    <Label className="text-amber-700">Tax (GST)</Label>
                    {canEditItems ? (
                      <Select value={taxRate.toString()} onValueChange={(v) => setTaxRate(parseFloat(v))}>
                        <SelectTrigger className="border-amber-200">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[0, 5, 12, 18, 28].map((r) => (
                            <SelectItem key={r} value={r.toString()}>{r}%</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-slate-700 font-medium">{taxRate}%</p>
                    )}
                  </div>
                  <div className="space-y-2 bg-slate-50 p-4 rounded-lg border border-slate-200">
                    <div className="flex justify-between text-sm"><span>Subtotal</span><span>{formatPrice(subtotal)}</span></div>
                    {discountAmount > 0 && (
                      <div className="flex justify-between text-sm text-green-600"><span>Discount</span><span>-{formatPrice(discountAmount)}</span></div>
                    )}
                    {taxAmount > 0 && (
                      <div className="flex justify-between text-sm"><span>GST ({taxRate}%)</span><span>+{formatPrice(taxAmount)}</span></div>
                    )}
                    <div className="border-t pt-2 mt-2">
                      <div className="flex justify-between font-bold text-lg">
                        <span>Total</span>
                        <span className="text-amber-600">{formatPrice(totalAmount)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Invoice Details */}
          <Card className="border-0 shadow-xl">
            <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-100">
              <CardTitle className="text-green-800">Invoice Details</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-green-700">Invoice Date</Label>
                  <Input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} className="border-green-200" />
                </div>
                <div>
                  <Label className="text-green-700">Due Date</Label>
                  <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="border-green-200" />
                </div>
              </div>
              <div>
                <Label className="text-green-700">Notes / Terms</Label>
                <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="border-green-200" />
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Package selector modal */}
      {showPackageSelector && canEditItems && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setShowPackageSelector(false)}
        >
          <div
            className="bg-white rounded-2xl max-w-4xl w-full flex flex-col max-h-[85vh] shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-5 border-b">
              <h2 className="text-xl font-bold">Select Item / Package</h2>
              <button onClick={() => setShowPackageSelector(false)} className="text-slate-500 hover:text-slate-700">
                ?
              </button>
            </div>
            <div className="p-5 border-b bg-slate-50">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                <Select value={selectedBuilding} onValueChange={setSelectedBuilding}>
                  <SelectTrigger><SelectValue placeholder="All Buildings" /></SelectTrigger>
                  <SelectContent>
                    {buildingOptions.map((opt) => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={selectedPackage} onValueChange={setSelectedPackage}>
                  <SelectTrigger><SelectValue placeholder="All Types" /></SelectTrigger>
                  <SelectContent>
                    {packageOptions.map((opt) => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              {priceLoading ? (
                <div className="text-center py-10 text-slate-500">Loading...</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {filteredPackages.map((pkg) => (
                    <div
                      key={pkg.id}
                      className="border rounded-xl p-4 hover:shadow-lg transition cursor-pointer"
                      onClick={() => addItemFromPackage(pkg)}
                    >
                      <h3 className="font-bold">{pkg.name}</h3>
                      <p className="text-sm text-slate-500">{pkg.buildingType} / {pkg.packageType}</p>
                      <p className="text-amber-600 font-semibold mt-1">{formatPrice(pkg.sellingPrice)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceForm;